import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  addStaffMember,
  deleteStaffMember,
  updateStaffRole,
  updateStaffList,
  createWorkgroup,
  getUserRole,
  fetchUserWorkgroups,
  getWorkgroupTypes,
  saveWorkgroupTypes,
  checkLicenseAllowsWorkgroups,
} from "./firestoreFunctions";
import { getToken, getUserId } from "../firebase";
import ApiKeysModern from "./ApiKeysModern";
import LicenseInfo from "./LicenseInfo";
import SettingsPage from "./SettingsPage";
import { listIntegrationSummaries } from "../api/integrations";
import {
  MasterDetailLayout,
  MasterListItem,
  DetailPanelHeader,
  EmptyState,
} from "./MasterDetailLayout";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { WorkgroupSelect } from "./WorkgroupSelect";
import IntegrationSelectionList, {
  type IntegrationSelectionOption,
} from "./IntegrationSelectionList";
import {
  Users,
  Key,
  ShieldCheck,
  Layers,
  Plug,
  PlusCircle,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  Home,
  Trash2,
  ListChecks,
  Settings as SettingsIcon,
  CheckCircle,
  AlertCircle,
  Clock,
  UserPlus,
} from "lucide-react";
import { cn } from "../lib/utils";
import DetailSummaryCard, {
  DetailSummarySection,
  DetailSummaryStatus,
  DetailSummaryTag,
} from "./detail/DetailSummaryCard";
import DetailSection from "./detail/DetailSection";

type AdminSectionId =
  | "members"
  | "workgroupType"
  | "apiKeys"
  | "userKeys"
  | "license"
  | "createWorkgroup"
  | "deleteWorkgroup";

type MasterItemKey = `section:${AdminSectionId}` | `integration:${string}`;

interface StaffMember {
  id: string;
  email: string;
  role: string;
  createdAt?: unknown;
}

type IntegrationOption = IntegrationSelectionOption;

interface AdminWorkgroup {
  id: string;
  name: string;
  description?: string;
}

interface LocationState {
  workgroupId?: string;
  userId?: string;
  section?: MasterItemKey;
}

interface AdminPageProps {
  user?: any;
}

const SECTION_KEY_MAP: Record<string, MasterItemKey> = {
  members: "section:members",
  "section:members": "section:members",
  workgroupType: "section:workgroupType",
  "section:workgroupType": "section:workgroupType",
  apiKeys: "section:apiKeys",
  "section:apiKeys": "section:apiKeys",
  license: "section:license",
  "section:license": "section:license",
  userKeys: "section:userKeys",
  "section:userKeys": "section:userKeys",
  createWorkgroup: "section:createWorkgroup",
  "section:createWorkgroup": "section:createWorkgroup",
  deleteWorkgroup: "section:deleteWorkgroup",
  "section:deleteWorkgroup": "section:deleteWorkgroup",
};

const resolveSectionKey = (
  value?: string | null
): MasterItemKey | null => {
  if (!value) {
    return null;
  }
  return SECTION_KEY_MAP[value] ?? null;
};

interface FeedbackBannerProps {
  type: "success" | "error";
  message: string;
  onClose: () => void;
}

const integrationGlyphMap: Record<string, string> = {
  aws: "☁️",
  salesforce: "💼",
  sf: "💼",
  woocommerce: "🛒",
  wc: "🛒",
  maximo: "🏭",
  msgraph: "📊",
  hubspot: "📈",
};

const getIntegrationGlyph = (integration: IntegrationOption): string => {
  const icon = (integration as any)?.icon as string | undefined;
  if (icon && icon.trim().length > 0) {
    return icon;
  }
  const id = (integration.id || "").toLowerCase();
  return integrationGlyphMap[id] || "🔌";
};

const truncateText = (value: string | undefined, max = 160) => {
  if (!value) return "";
  const trimmed = value.trim();
  if (trimmed.length <= max) return trimmed;
  return `${trimmed.slice(0, max)}…`;
};

const formatIntegrationStatusLabel = (
  status?: string,
  enabled?: boolean
) => {
  const normalized = status?.trim();
  if (normalized && normalized.length > 0) {
    return normalized.charAt(0).toUpperCase() + normalized.slice(1);
  }
  return enabled ? "Enabled" : "Disabled";
};

const resolveIntegrationStatusTone = (
  status?: string,
  enabled?: boolean
): DetailSummaryStatus["tone"] => {
  const normalized = (status || "").toLowerCase();
  switch (normalized) {
    case "active":
    case "enabled":
      return "success";
    case "inactive":
    case "disabled":
      return "danger";
    case "draft":
      return "warning";
    default:
      return enabled ? "success" : "warning";
  }
};

const resolveIntegrationStatusIcon = (status?: string) => {
  const normalized = (status || "").toLowerCase();
  switch (normalized) {
    case "inactive":
    case "disabled":
      return AlertCircle;
    case "draft":
      return Clock;
    default:
      return CheckCircle;
  }
};

const formatIntegrationUpdatedAt = (value?: string) => {
  if (!value) return "Unknown";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Unknown";
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const FeedbackBanner: React.FC<FeedbackBannerProps> = ({
  type,
  message,
  onClose,
}) => {
  const isSuccess = type === "success";
  const icon = isSuccess ? (
    <CheckCircle2 className="h-5 w-5 text-emerald-500" />
  ) : (
    <AlertTriangle className="h-5 w-5 text-destructive" />
  );

  return (
    <div
      className={cn(
        "flex items-start gap-3 rounded-md border px-4 py-3 text-sm",
        isSuccess
          ? "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/40 dark:bg-emerald-950/40 dark:text-emerald-300"
          : "border-destructive/40 bg-destructive/10 text-destructive dark:border-destructive/30 dark:bg-destructive/10"
      )}
    >
      {icon}
      <div className="flex-1">
        <p className="font-medium">
          {isSuccess ? "Success" : "Something went wrong"}
        </p>
        <p className="mt-1 text-sm text-muted-foreground dark:text-slate-300">
          {message}
        </p>
      </div>
      <Button
        variant="ghost"
        size="sm"
        onClick={onClose}
        className="ml-2 shrink-0"
      >
        Dismiss
      </Button>
    </div>
  );
};

const SidebarSectionLabel: React.FC<{ label: string }> = ({ label }) => (
  <div className="px-4 pt-5 pb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
    {label}
  </div>
);

interface UserConfigEntry {
  email: string;
  config: Record<string, Record<string, string>>;
  updated_by: string | null;
  updatedAt: any;
}

const UserSettingsTable: React.FC<{ backendUrl: string; workgroupId: string }> = ({
  backendUrl,
  workgroupId,
}) => {
  const [userConfigs, setUserConfigs] = useState<UserConfigEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!workgroupId) return;
    let cancelled = false;

    const fetchConfigs = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = await getToken();
        const res = await fetch(
          `${backendUrl}/v1/user-configs?workgroupId=${encodeURIComponent(workgroupId)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error || `Failed to fetch user configs (${res.status})`);
        }
        const data = await res.json();
        if (!cancelled) setUserConfigs(data.userConfigs || []);
      } catch (err: any) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchConfigs();
    return () => { cancelled = true; };
  }, [backendUrl, workgroupId]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-8 justify-center text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading user settings…
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
        {error}
      </div>
    );
  }

  if (userConfigs.length === 0) {
    return (
      <div className="py-8 text-center text-sm text-muted-foreground">
        No users have individual settings configured yet.
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="user-settings-table">
      {userConfigs
        .sort((a, b) => a.email.localeCompare(b.email))
        .map((uc) => (
          <div key={uc.email} className="rounded-lg border bg-white p-4">
            <div className="flex items-center gap-2 mb-3">
              <Users className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">{uc.email}</span>
            </div>
            {Object.entries(uc.config).map(([integrationType, fields]) => (
              <div key={integrationType} className="mb-3 last:mb-0">
                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                  {integrationType}
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-xs text-muted-foreground">
                      <th className="pb-2 font-medium">Field</th>
                      <th className="pb-2 font-medium">Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(fields || {}).map(([field, value]) => (
                      <tr key={field} className="border-b last:border-0">
                        <td className="py-2 text-muted-foreground">{field}</td>
                        <td className="py-2 font-mono text-xs">{String(value)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
            {uc.updatedAt && (
              <div className="mt-2 text-xs text-muted-foreground">
                Last updated: {new Date(
                  typeof uc.updatedAt === "object" && uc.updatedAt._seconds
                    ? uc.updatedAt._seconds * 1000
                    : uc.updatedAt
                ).toLocaleString()}
              </div>
            )}
          </div>
        ))}
    </div>
  );
};

const AdminPage: React.FC<AdminPageProps> = ({ user }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const locationState = (location.state as LocationState | undefined) ?? {};

  const [initialWorkgroupId] = useState<string | null>(
    locationState.workgroupId ?? null
  );

  const initialSectionFromState = resolveSectionKey(locationState.section);
  const initialSectionFromQuery = resolveSectionKey(
    new URLSearchParams(location.search).get("section")
  );
  const initialResolvedSection =
    initialSectionFromQuery ?? initialSectionFromState ?? null;

  const [activeItemKey, setActiveItemKey] = useState<MasterItemKey>(
    initialResolvedSection ?? "section:members"
  );
  const [pendingSectionKey, setPendingSectionKey] = useState<MasterItemKey | null>(
    initialResolvedSection
  );

  const [availableWorkgroups, setAvailableWorkgroups] = useState<
    AdminWorkgroup[]
  >([]);
  const [workgroupsLoading, setWorkgroupsLoading] = useState(false);
  const [selectedWorkgroupId, setSelectedWorkgroupId] = useState<string | null>(
    initialWorkgroupId
  );

  const [workgroupTypes, setWorkgroupTypes] = useState<string[]>([]);
  const [typesLoading, setTypesLoading] = useState(false);
  const [updatingIntegrationId, setUpdatingIntegrationId] = useState<
    string | null
  >(null);

  const [availableIntegrations, setAvailableIntegrations] = useState<
    IntegrationOption[]
  >([]);
  const [integrationsLoading, setIntegrationsLoading] = useState(true);

  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [staffSearch, setStaffSearch] = useState("");
  const [newStaffEmail, setNewStaffEmail] = useState("");

  const [newWorkgroupName, setNewWorkgroupName] = useState("");
  const [creatingWorkgroup, setCreatingWorkgroup] = useState(false);
  const [deletingWorkgroup, setDeletingWorkgroup] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [isAdmin, setIsAdmin] = useState(false);

  const currentUserId =
    (user as any)?.user?.uid ??
    (user as any)?.uid ??
    locationState.userId ??
    null;

  const currentWorkgroupId =
    selectedWorkgroupId ??
    initialWorkgroupId ??
    (availableWorkgroups[0]?.id ?? null);

  const currentWorkgroup =
    currentWorkgroupId != null
      ? availableWorkgroups.find((wg) => wg.id === currentWorkgroupId)
      : undefined;
  const currentWorkgroupName =
    currentWorkgroup?.name ?? currentWorkgroupId ?? "Unknown Workgroup";

  const llmUrl = `${window.location.protocol}//${window.location.hostname}/api/llm`;
  const backendUrl = `${window.location.protocol}//${window.location.hostname}/api/server`;

  const showError = (message: string) => {
    setError(message);
    setSuccessMessage(null);
  };

  const showSuccess = (message: string) => {
    setSuccessMessage(message);
    setError(null);
  };

  const clearFeedback = () => {
    setError(null);
    setSuccessMessage(null);
  };

  const refreshWorkgroups = useCallback(async () => {
    setWorkgroupsLoading(true);
    const fallback = initialWorkgroupId
      ? [{ id: initialWorkgroupId, name: initialWorkgroupId }]
      : [];

    try {
      const authenticatedUserId = await getUserId();
      if (!authenticatedUserId) {
        setAvailableWorkgroups(fallback);
        return;
      }

      const userWorkgroupIds = await fetchUserWorkgroups(authenticatedUserId);

      if (Array.isArray(userWorkgroupIds) && userWorkgroupIds.length > 0) {
        const formattedWorkgroups: AdminWorkgroup[] = userWorkgroupIds.map(
          (id: string) => ({
            id,
            name: id,
          })
        );

        if (
          initialWorkgroupId &&
          !formattedWorkgroups.some((wg) => wg.id === initialWorkgroupId)
        ) {
          formattedWorkgroups.push({
            id: initialWorkgroupId,
            name: initialWorkgroupId,
          });
        }

        setAvailableWorkgroups(formattedWorkgroups);
      } else {
        setAvailableWorkgroups(fallback);
      }
    } catch (err) {
      console.error("AdminPage: failed to fetch workgroups", err);
      if (fallback.length > 0) {
        setAvailableWorkgroups(fallback);
      }
    } finally {
      setWorkgroupsLoading(false);
    }
  }, [initialWorkgroupId]);

  useEffect(() => {
    refreshWorkgroups();
  }, [refreshWorkgroups]);

  useEffect(() => {
    if (!selectedWorkgroupId && availableWorkgroups.length > 0) {
      setSelectedWorkgroupId(availableWorkgroups[0].id);
    }
  }, [availableWorkgroups, selectedWorkgroupId]);

  useEffect(() => {
    if (!selectedWorkgroupId) {
      setAvailableIntegrations([]);
      setIntegrationsLoading(false);
      return;
    }

    let cancelled = false;

    const fetchIntegrations = async () => {
      setIntegrationsLoading(true);
      try {
        const integrations = await listIntegrationSummaries(selectedWorkgroupId);
        if (!cancelled) {
          if (Array.isArray(integrations)) {
            setAvailableIntegrations(integrations as IntegrationOption[]);
          } else {
            setAvailableIntegrations([]);
          }
        }
      } catch (err) {
        console.error("AdminPage: failed to fetch integrations", err);
        if (!cancelled) {
          setAvailableIntegrations([]);
        }
      } finally {
        if (!cancelled) {
          setIntegrationsLoading(false);
        }
      }
    };

    fetchIntegrations();

    return () => {
      cancelled = true;
    };
  }, [selectedWorkgroupId]);

  useEffect(() => {
    if (!currentWorkgroupId) {
      setWorkgroupTypes([]);
      return;
    }

    let cancelled = false;

    const loadTypes = async () => {
      setTypesLoading(true);
      try {
        const types = await getWorkgroupTypes(currentWorkgroupId);
        if (!cancelled) {
          setWorkgroupTypes(Array.isArray(types) ? types : []);
        }
      } catch (err) {
        console.error("AdminPage: failed to load workgroup types", err);
        if (!cancelled) {
          setWorkgroupTypes([]);
        }
      } finally {
        if (!cancelled) {
          setTypesLoading(false);
        }
      }
    };

    loadTypes();
    return () => {
      cancelled = true;
    };
  }, [currentWorkgroupId]);

  useEffect(() => {
    if (!currentWorkgroupId || !currentUserId) {
      setIsAdmin(false);
      return;
    }

    let cancelled = false;

    const fetchRole = async () => {
      try {
        const role = await getUserRole(currentWorkgroupId, currentUserId);
        console.log(`User role ${role}`)
        if (!cancelled) {
          setIsAdmin(role === "admin" || role === "owner");
        }
      } catch (err) {
        console.error("AdminPage: failed to fetch user role", err);
        if (!cancelled) {
          setIsAdmin(false);
        }
      }
    };

    fetchRole();
    return () => {
      cancelled = true;
    };
  }, [currentWorkgroupId, currentUserId]);

  useEffect(() => {
    if (!currentWorkgroupId) {
      setStaff([]);
      return;
    }

    if (!activeItemKey.startsWith("section:members")) {
      return;
    }

    updateStaffList(currentWorkgroupId, setStaff, setError, setMembersLoading);
  }, [currentWorkgroupId, activeItemKey]);

  useEffect(() => {
    clearFeedback();
  }, [activeItemKey]);

  const handleWorkgroupChange = (newWorkgroupId: string) => {
    if (!newWorkgroupId || newWorkgroupId === currentWorkgroupId) return;
    if (!availableWorkgroups.some((wg) => wg.id === newWorkgroupId)) {
      showError("Invalid workgroup selection");
      return;
    }
    setSelectedWorkgroupId(newWorkgroupId);
    setStaff([]);
    clearFeedback();
    setActiveItemKey("section:members");
  };

  const handleAddStaff = async () => {
    if (!currentWorkgroupId) {
      showError("Select a workgroup before adding members");
      return;
    }

    if (!newStaffEmail.trim()) {
      showError("Please enter an email address");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newStaffEmail.trim())) {
      showError("Please enter a valid email address");
      return;
    }

    try {
      setMembersLoading(true);
      await addStaffMember(
        currentWorkgroupId,
        newStaffEmail.trim(),
        "member",
        setError
      );
      setNewStaffEmail("");
      showSuccess("Team member added successfully");
      await updateStaffList(
        currentWorkgroupId,
        setStaff,
        setError,
        setMembersLoading
      );
    } catch (err: any) {
      console.error("AdminPage: failed to add staff member", err);
      showError(err?.message || "Failed to add team member");
      setMembersLoading(false);
    }
  };

  const handleRemoveStaff = async (member: StaffMember) => {
    if (!currentWorkgroupId) {
      showError("Select a workgroup before removing members");
      return;
    }

    const confirmed = window.confirm(
      `Remove ${member.email} from ${currentWorkgroupName}?`
    );
    if (!confirmed) return;

    try {
      setMembersLoading(true);
      await deleteStaffMember(currentWorkgroupId, member.id);
      showSuccess("Team member removed successfully");
      await updateStaffList(
        currentWorkgroupId,
        setStaff,
        setError,
        setMembersLoading
      );
    } catch (err) {
      console.error("AdminPage: failed to remove staff member", err);
      showError("Failed to remove team member");
      setMembersLoading(false);
    }
  };

  const handleToggleRole = async (member: StaffMember) => {
    if (!currentWorkgroupId) {
      showError("Select a workgroup before updating roles");
      return;
    }

    const newRole = member.role === "admin" ? "member" : "admin";
    const action =
      newRole === "admin" ? "grant admin rights to" : "remove admin rights from";

    const confirmed = window.confirm(
      `Are you sure you want to ${action} ${member.email}?`
    );
    if (!confirmed) return;

    try {
      setMembersLoading(true);
      await updateStaffRole(currentWorkgroupId, member.id, newRole);
      setStaff((prev) =>
        prev.map((s) =>
          s.id === member.id
            ? {
                ...s,
                role: newRole,
              }
            : s
        )
      );
      showSuccess(
        `${member.email} is now ${newRole === "admin" ? "an admin" : "a member"}`
      );
    } catch (err) {
      console.error("AdminPage: failed to update staff role", err);
      showError("Failed to update member role");
    } finally {
      setMembersLoading(false);
    }
  };

  const handleWorkgroupTypeChange = async (
    integrationId: string,
    isChecked: boolean
  ) => {
    if (!currentWorkgroupId) {
      showError("Select a workgroup to update integrations");
      return;
    }

    const updatedTypes = isChecked
      ? Array.from(new Set([...workgroupTypes, integrationId]))
      : workgroupTypes.filter((type) => type !== integrationId);

    setUpdatingIntegrationId(integrationId);
    try {
      await saveWorkgroupTypes(currentWorkgroupId, updatedTypes);
      setWorkgroupTypes(updatedTypes);
      try {
        window.dispatchEvent(new Event("integrations-changed"));
      } catch {
        // ignore best-effort errors
      }
      showSuccess("Integration access updated");
    } catch (err) {
      console.error("AdminPage: failed to update workgroup types", err);
      showError("Failed to update integration access");
    } finally {
      setUpdatingIntegrationId(null);
    }
  };

  const handleCreateWorkgroup = async () => {
    const trimmedName = newWorkgroupName.trim();

    if (!trimmedName) {
      showError("Workgroup name cannot be empty");
      return;
    }

    if (!/^[a-zA-Z0-9 ]+$/.test(trimmedName)) {
      showError("Group names can only contain letters, numbers, and spaces");
      return;
    }

    if (!currentUserId) {
      showError("You must be signed in to create a workgroup");
      return;
    }

    const canCreate = await checkLicenseAllowsWorkgroups(currentUserId);
    if (!canCreate) {
      showError(
        "Your license has reached the maximum number of workgroups. Please upgrade to add more."
      );
      return;
    }

    setCreatingWorkgroup(true);
    try {
      await createWorkgroup(trimmedName);
      showSuccess(`Workgroup "${trimmedName}" created successfully`);
      setNewWorkgroupName("");
      await refreshWorkgroups();
    } catch (err) {
      console.error("AdminPage: failed to create workgroup", err);
      showError("Failed to create workgroup");
    } finally {
      setCreatingWorkgroup(false);
    }
  };

  const handleDeleteWorkgroup = async () => {
    if (!currentWorkgroupId) {
      showError("Select a workgroup to delete");
      return;
    }

    const workgroupLabel = currentWorkgroupName;
    const confirmation = window.prompt(
      `WARNING: This will permanently delete the workgroup "${workgroupLabel}" and all associated data.\n\nType "${workgroupLabel}" to confirm deletion:`
    );

    if (confirmation === null) {
      return;
    }

    if (confirmation !== workgroupLabel) {
      showError("Workgroup name does not match. Deletion canceled");
      return;
    }

    setDeletingWorkgroup(true);
    try {
      const response = await fetch(`${llmUrl}/v1/deleteWorkgroup`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          token: await getToken(),
          workgroup: currentWorkgroupId,
        }),
      });

      if (!response.ok) {
        throw new Error(await response.text());
      }

      showSuccess(`Workgroup "${workgroupLabel}" deleted successfully`);
      await refreshWorkgroups();
      setSelectedWorkgroupId(null);
      setActiveItemKey("section:members");

      setTimeout(() => {
        navigate("/");
      }, 1500);
    } catch (err) {
      console.error("AdminPage: failed to delete workgroup", err);
      showError("Failed to delete workgroup");
    } finally {
      setDeletingWorkgroup(false);
    }
  };

  const filteredStaff = useMemo(() => {
    const query = staffSearch.trim().toLowerCase();
    if (!query) return staff;
    return staff.filter((member) =>
      member.email?.toLowerCase().includes(query)
    );
  }, [staff, staffSearch]);

  const enabledIntegrations = useMemo(() => {
    return new Set(workgroupTypes);
  }, [workgroupTypes]);

  const formatIntegrationName = (integration: IntegrationOption) => {
    if (integration.name) return integration.name;
    return integration.id.charAt(0).toUpperCase() + integration.id.slice(1);
  };

  const generalItems = useMemo(() => {
    const items: Array<{
      key: MasterItemKey;
      title: string;
      subtitle?: string;
      icon: React.ReactNode;
      badge?: React.ReactNode;
    }> = [
      {
        key: "section:members",
        title: "Team Members",
        subtitle: `${staff.length} member${staff.length === 1 ? "" : "s"}`,
        icon: <Users className="h-4 w-4" />,
      },
    ];

    if (isAdmin) {
      items.push({
        key: "section:workgroupType",
        title: "Integration Access",
        subtitle: "Enable integrations for this workgroup",
        icon: <ListChecks className="h-4 w-4" />,
        badge:
          workgroupTypes.length > 0 ? (
            <Badge variant="secondary">{workgroupTypes.length} enabled</Badge>
          ) : (
            <Badge variant="outline">None</Badge>
          ),
      });
    }

    return items;
  }, [isAdmin, staff.length, workgroupTypes.length]);

  const securityItems = useMemo(() => {
    if (!isAdmin) return [];
    return [
      {
        key: "section:apiKeys" as MasterItemKey,
        title: "API Keys",
        subtitle: "Manage credentials and tokens",
        icon: <Key className="h-4 w-4" />,
      },
      {
        key: "section:userKeys" as MasterItemKey,
        title: "User Settings",
        subtitle: "View individual user configurations",
        icon: <Users className="h-4 w-4" />,
      },
      {
        key: "section:license" as MasterItemKey,
        title: "License",
        subtitle: "View license usage and limits",
        icon: <ShieldCheck className="h-4 w-4" />,
      },
    ];
  }, [isAdmin]);

  const integrationItems = useMemo(() => {
    if (!isAdmin) return [];

    const enabledList = availableIntegrations.filter((integration) =>
      enabledIntegrations.has(integration.id)
    );

    return enabledList
      .map((integration) => ({
        key: `integration:${integration.id}` as MasterItemKey,
        title: `${formatIntegrationName(integration)} Settings`,
        subtitle:
          integration.description ||
          "Configure credentials and connection details",
        icon: <Plug className="h-4 w-4" />,
        badge: <Badge variant="secondary">Enabled</Badge>,
      }))
      .sort((a, b) => a.title.localeCompare(b.title));
  }, [availableIntegrations, enabledIntegrations, isAdmin]);

  const actionItems = useMemo(() => {
    if (!isAdmin) return [];
    return [
      {
        key: "section:createWorkgroup" as MasterItemKey,
        title: "Create Workgroup",
        subtitle: "Spin up a new collaborative space",
        icon: <PlusCircle className="h-4 w-4" />,
      },
      {
        key: "section:deleteWorkgroup" as MasterItemKey,
        title: "Delete Workgroup",
        subtitle: "Danger zone — permanent action",
        icon: <Trash2 className="h-4 w-4" />,
      },
    ];
  }, [isAdmin]);

  const allItems = useMemo(
    () => [...generalItems, ...securityItems, ...integrationItems, ...actionItems],
    [generalItems, securityItems, integrationItems, actionItems]
  );

  useEffect(() => {
    if (pendingSectionKey) {
      if (allItems.some((item) => item.key === pendingSectionKey)) {
        setActiveItemKey(pendingSectionKey);
        setPendingSectionKey(null);
        return;
      }
    }

    if (allItems.length === 0) return;
    if (!allItems.some((item) => item.key === activeItemKey)) {
      setActiveItemKey(allItems[0].key);
    }
  }, [allItems, activeItemKey, pendingSectionKey]);

  useEffect(() => {
    const sectionFromQuery = resolveSectionKey(
      new URLSearchParams(location.search).get("section")
    );
    if (
      sectionFromQuery &&
      sectionFromQuery !== activeItemKey &&
      sectionFromQuery !== pendingSectionKey
    ) {
      setPendingSectionKey(sectionFromQuery);
    }
  }, [location.search, activeItemKey, pendingSectionKey]);

  const activeIntegrationId = activeItemKey.startsWith("integration:")
    ? activeItemKey.split(":")[1]
    : null;
  const activeSectionId: AdminSectionId | null = activeIntegrationId
    ? null
    : (activeItemKey.replace("section:", "") as AdminSectionId);

  const feedbackContent = (
    <>
      {error && (
        <FeedbackBanner type="error" message={error} onClose={clearFeedback} />
      )}
      {successMessage && (
        <FeedbackBanner
          type="success"
          message={successMessage}
          onClose={clearFeedback}
        />
      )}
    </>
  );

  const renderMembers = () => {
    const canManageMembers = isAdmin;

    return (
      <div className="flex h-full flex-col" style={{ backgroundColor: "#e8effa" }}>
        <DetailPanelHeader
          title="Team Members"
          subtitle={`Manage access for ${currentWorkgroupName}`}
          icon={<Users className="h-6 w-6 text-primary" />}
          actions={
            canManageMembers ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setActiveItemKey("section:workgroupType")}
              >
                <Layers className="h-4 w-4" />
                Integration access
              </Button>
            ) : undefined
          }
        />
        <div className="flex-1 overflow-y-auto">
          <div className="mx-auto w-full max-w-5xl space-y-6 px-6 py-8">
            {feedbackContent}

            {canManageMembers ? (
              <DetailSection
                icon={<UserPlus className="h-5 w-5 text-primary" />}
                title="Invite team member"
                subtitle="Send an invitation to collaborate in this workgroup."
              >
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                  <Input
                    type="email"
                    value={newStaffEmail}
                    onChange={(event) => setNewStaffEmail(event.target.value)}
                    placeholder="person@company.com"
                    disabled={membersLoading}
                    className="sm:flex-1"
                  />
                  <Button
                    onClick={handleAddStaff}
                    disabled={membersLoading}
                    className="sm:w-auto"
                  >
                    {membersLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Adding…
                      </>
                    ) : (
                      "Invite member"
                    )}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Invited teammates receive an email with setup instructions.
                </p>
              </DetailSection>
            ) : (
              <DetailSection
                icon={<Users className="h-5 w-5 text-primary" />}
                title="View-only access"
                subtitle="You can review the roster for this workgroup."
              >
                <p className="text-sm text-muted-foreground">
                  Contact an administrator if you need to invite or update members.
                </p>
              </DetailSection>
            )}

            <DetailSection
              icon={<ListChecks className="h-5 w-5 text-primary" />}
              title="Current members"
              subtitle="Only admins can change roles or remove members."
              toolbar={<Badge variant="outline">{staff.length} listed</Badge>}
            >
              <Input
                placeholder="Search by email"
                value={staffSearch}
                onChange={(event) => setStaffSearch(event.target.value)}
              />
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-border text-sm">
                  <thead className="bg-muted/40">
                    <tr>
                      <th className="px-4 py-2 text-left font-medium text-muted-foreground">
                        Email
                      </th>
                      <th className="px-4 py-2 text-left font-medium text-muted-foreground">
                        Role
                      </th>
                      <th className="px-4 py-2 text-right font-medium text-muted-foreground">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {membersLoading ? (
                      <tr>
                        <td
                          colSpan={3}
                          className="px-4 py-6 text-center text-muted-foreground"
                        >
                          <div className="flex items-center justify-center gap-2 text-sm">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Loading team members…
                          </div>
                        </td>
                      </tr>
                    ) : filteredStaff.length === 0 ? (
                      <tr>
                        <td
                          colSpan={3}
                          className="px-4 py-6 text-center text-muted-foreground"
                        >
                          {!staffSearch
                            ? "No team members found."
                            : "No matches for that search."}
                        </td>
                      </tr>
                    ) : (
                      filteredStaff.map((member) => (
                        <tr key={member.id}>
                          <td className="px-4 py-2">{member.email}</td>
                          <td className="px-4 py-2">
                            <Badge
                              variant={
                                member.role === "admin" ? "secondary" : "outline"
                              }
                            >
                              {member.role}
                            </Badge>
                          </td>
                          <td className="px-4 py-2">
                            <div className="flex justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleToggleRole(member)}
                                disabled={membersLoading || !isAdmin}
                              >
                                {member.role === "admin"
                                  ? "Remove admin"
                                  : "Make admin"}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-destructive hover:bg-destructive/10"
                                onClick={() => handleRemoveStaff(member)}
                                disabled={membersLoading || !isAdmin}
                              >
                                Remove
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </DetailSection>
          </div>
        </div>
      </div>
    );
  };

  const renderIntegrationAccess = () => {
    if (!isAdmin) {
      return (
        <div className="flex h-full flex-col" style={{ backgroundColor: "#e8effa" }}>
          <DetailPanelHeader
            title="Integration Access"
            subtitle="Only administrators can update integration access."
            icon={<Layers className="h-6 w-6 text-primary" />}
          />
          <div className="flex-1">
            <EmptyState
              title="Restricted access"
              description="Contact an administrator to request changes to integration access."
            />
          </div>
        </div>
      );
    }

    return (
      <div className="flex h-full flex-col" style={{ backgroundColor: "#e8effa" }}>
        <DetailPanelHeader
          title="Integration Access"
          subtitle={`Enable integrations for ${currentWorkgroupName}`}
          icon={<Layers className="h-6 w-6 text-primary" />}
        />
        <div className="flex-1 overflow-y-auto">
          <div className="mx-auto w-full max-w-5xl space-y-6 px-6 py-8">
            {feedbackContent}

            <DetailSection
              icon={<Plug className="h-5 w-5 text-primary" />}
              title="Available integrations"
              subtitle="Toggle access to make integrations available to this workgroup’s members."
            >
              <IntegrationSelectionList
                integrations={availableIntegrations}
                selectedIntegrationIds={enabledIntegrations}
                onToggle={(integrationId, checked) =>
                  handleWorkgroupTypeChange(integrationId, checked)
                }
                loading={integrationsLoading}
                pendingIntegrationId={updatingIntegrationId}
                formatIntegrationName={formatIntegrationName}
              />
            </DetailSection>
          </div>
        </div>
      </div>
    );
  };

  const renderApiKeys = () => {
    if (!isAdmin) {
      return (
        <div className="flex h-full flex-col" style={{ backgroundColor: "#e8effa" }}>
          <DetailPanelHeader
            title="API Keys"
            subtitle="Only administrators can manage API keys."
            icon={<Key className="h-6 w-6 text-primary" />}
          />
          <div className="flex-1">
            <EmptyState
              title="Restricted access"
              description="Contact an administrator to request changes to API keys."
            />
          </div>
        </div>
      );
    }

    return (
      <div className="flex h-full flex-col" style={{ backgroundColor: "#e8effa" }}>
        <DetailPanelHeader
          title="API Keys"
          subtitle="Securely manage service credentials for this workgroup"
          icon={<Key className="h-6 w-6 text-primary" />}
        />
        <div className="flex-1 overflow-y-auto">
          <div className="mx-auto w-full max-w-5xl space-y-6 px-6 py-8">
            {feedbackContent}
            <DetailSection
              icon={<Key className="h-5 w-5 text-primary" />}
              title="Credential management"
              subtitle="Generate, rotate, and revoke API keys for this workgroup."
            >
              <ApiKeysModern
                backendUrl={backendUrl}
                userId={currentUserId}
                workgroupId={currentWorkgroupId || ""}
                workgroupName={currentWorkgroupName}
                llmUrl={llmUrl}
              />
            </DetailSection>
          </div>
        </div>
      </div>
    );
  };

  const renderUserKeys = () => {
    if (!isAdmin) {
      return (
        <div className="flex h-full flex-col" style={{ backgroundColor: "#e8effa" }}>
          <DetailPanelHeader
            title="User Settings"
            subtitle="Only administrators can view user settings."
            icon={<Users className="h-6 w-6 text-primary" />}
          />
          <div className="flex-1">
            <EmptyState
              title="Restricted access"
              description="Contact an administrator to view user settings."
            />
          </div>
        </div>
      );
    }

    return (
      <div className="flex h-full flex-col" style={{ backgroundColor: "#e8effa" }}>
        <DetailPanelHeader
          title="User Settings"
          subtitle="View individual configuration values set per user"
          icon={<Users className="h-6 w-6 text-primary" />}
        />
        <div className="flex-1 overflow-y-auto">
          <div className="mx-auto w-full max-w-5xl space-y-6 px-6 py-8">
            {feedbackContent}
            <DetailSection
              icon={<Users className="h-5 w-5 text-primary" />}
              title="User configurations"
              subtitle="Individual settings saved per user via the API."
            >
              <UserSettingsTable
                backendUrl={backendUrl}
                workgroupId={currentWorkgroupId || ""}
              />
            </DetailSection>
          </div>
        </div>
      </div>
    );
  };

  const renderLicense = () => {
    if (!isAdmin) {
      return (
        <div className="flex h-full flex-col" style={{ backgroundColor: "#e8effa" }}>
          <DetailPanelHeader
            title="License"
            subtitle="Only administrators can view license information."
            icon={<ShieldCheck className="h-6 w-6 text-primary" />}
          />
          <div className="flex-1">
            <EmptyState
              title="Restricted access"
              description="Contact an administrator to view license information."
            />
          </div>
        </div>
      );
    }

    return (
      <div className="flex h-full flex-col" style={{ backgroundColor: "#e8effa" }}>
        <DetailPanelHeader
          title="License"
          subtitle="Monitor usage and entitlements for this workgroup"
          icon={<ShieldCheck className="h-6 w-6 text-primary" />}
        />
        <div className="flex-1 overflow-y-auto">
          <div className="mx-auto w-full max-w-5xl space-y-6 px-6 py-8">
            {feedbackContent}
            <DetailSection
              icon={<ShieldCheck className="h-5 w-5 text-primary" />}
              title="License overview"
              subtitle="Track consumption, limits, and renewal details."
            >
              <LicenseInfo workgroupId={currentWorkgroupId || ""} />
            </DetailSection>
          </div>
        </div>
      </div>
    );
  };

  const renderCreateWorkgroup = () => {
    if (!isAdmin) {
      return (
        <div className="flex h-full flex-col" style={{ backgroundColor: "#e8effa" }}>
          <DetailPanelHeader
            title="Create Workgroup"
            subtitle="Only administrators can create new workgroups."
            icon={<PlusCircle className="h-6 w-6 text-primary" />}
          />
          <div className="flex-1">
            <EmptyState
              title="Restricted access"
              description="Contact an administrator to request a new workgroup."
            />
          </div>
        </div>
      );
    }

    return (
      <div className="flex h-full flex-col" style={{ backgroundColor: "#e8effa" }}>
        <DetailPanelHeader
          title="Create Workgroup"
          subtitle="Spin up a dedicated space for a new team or client"
          icon={<PlusCircle className="h-6 w-6 text-primary" />}
        />
        <div className="flex-1 overflow-y-auto">
          <div className="mx-auto w-full max-w-3xl space-y-6 px-6 py-8">
            {feedbackContent}

            <DetailSection
              icon={<PlusCircle className="h-5 w-5 text-primary" />}
              title="Workgroup details"
              subtitle="Workgroup names can include letters, numbers, and spaces."
            >
              <Input
                value={newWorkgroupName}
                onChange={(event) => setNewWorkgroupName(event.target.value)}
                placeholder="E.g. Northwind Sales"
                disabled={creatingWorkgroup}
              />
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-xs text-muted-foreground">
                  You will automatically become an admin of the new workgroup.
                </p>
                <Button onClick={handleCreateWorkgroup} disabled={creatingWorkgroup}>
                  {creatingWorkgroup ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Creating…
                    </>
                  ) : (
                    "Create workgroup"
                  )}
                </Button>
              </div>
            </DetailSection>
          </div>
        </div>
      </div>
    );
  };

  const renderDeleteWorkgroup = () => {
    if (!isAdmin) {
      return (
        <div className="flex h-full flex-col" style={{ backgroundColor: "#e8effa" }}>
          <DetailPanelHeader
            title="Delete Workgroup"
            subtitle="Only administrators can delete workgroups."
            icon={<Trash2 className="h-6 w-6 text-primary" />}
          />
          <div className="flex-1">
            <EmptyState
              title="Restricted access"
              description="Contact an administrator if this workgroup should be deleted."
            />
          </div>
        </div>
      );
    }

    return (
      <div className="flex h-full flex-col" style={{ backgroundColor: "#e8effa" }}>
        <DetailPanelHeader
          title="Delete Workgroup"
          subtitle={`Delete ${currentWorkgroupName} and all associated data`}
          icon={<Trash2 className="h-6 w-6 text-destructive" />}
          actions={
            <Button variant="outline" size="sm" onClick={() => setActiveItemKey("section:members")}>
              Cancel
            </Button>
          }
        />
        <div className="flex-1 overflow-y-auto">
          <div className="mx-auto w-full max-w-3xl space-y-6 px-6 py-8">
            {feedbackContent}

            <DetailSection
              className="border-destructive/40 bg-destructive/5"
              icon={<Trash2 className="h-5 w-5 text-destructive" />}
              title="This action cannot be undone"
              subtitle="Deleting a workgroup permanently removes conversations, integrations, API keys, and member access."
            >
              <ul className="list-disc space-y-2 pl-5 text-sm text-destructive">
                <li>All historical chats and training data will be removed.</li>
                <li>Integrations and API keys associated with this workgroup will be deleted.</li>
                <li>Members will immediately lose access and their navigation links will break.</li>
              </ul>
              <Button
                variant="destructive"
                onClick={handleDeleteWorkgroup}
                disabled={deletingWorkgroup}
              >
                {deletingWorkgroup ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Deleting…
                  </>
                ) : (
                  "Delete workgroup"
                )}
              </Button>
            </DetailSection>
          </div>
        </div>
      </div>
    );
  };

  const renderIntegrationDetail = (integrationId: string) => {
    const integration = availableIntegrations.find(
      (item) => item.id === integrationId
    );

    if (!integration) {
      return (
        <div className="flex h-full flex-col">
          <DetailPanelHeader
            title="Integration unavailable"
            subtitle="The requested integration could not be found."
            icon={<Plug className="h-6 w-6 text-primary" />}
          />
          <div className="flex-1">
            <EmptyState
              title="Integration not found"
              description="This integration may have been removed or renamed."
              action={
                <Button onClick={() => setActiveItemKey("section:workgroupType")}>
                  Manage integrations
                </Button>
              }
            />
          </div>
        </div>
      );
    }

    if (!isAdmin) {
      return (
        <div className="flex h-full flex-col">
          <DetailPanelHeader
            title={`${formatIntegrationName(integration)} Settings`}
            subtitle="Only administrators can configure integrations."
            icon={<Plug className="h-6 w-6 text-primary" />}
          />
          <div className="flex-1">
            <EmptyState
              title="Restricted access"
              description="Contact an administrator to update integration settings."
            />
          </div>
        </div>
      );
    }

    const enabled = enabledIntegrations.has(integration.id);
    const integrationAny = integration as Record<string, unknown>;
    const systemPrompt = integrationAny["system_prompt"] as string | undefined;
    const cddiInstructions = integrationAny["cddi_instructions"] as
      | string
      | undefined;
    const statusValue = integrationAny["status"] as string | undefined;
    const updatedAt = integrationAny["updated_at"] as string | undefined;
    const ownerType = integrationAny["owner_type"] as string | undefined;
    const visibility = integrationAny["visibility"] as string | undefined;

    const summarySections: DetailSummarySection[] = [
      {
        title: "System Prompt",
        content: systemPrompt ? (
          <p className="line-clamp-3 text-sm text-muted-foreground">
            {truncateText(systemPrompt, 220)}
          </p>
        ) : (
          <p className="text-sm italic text-muted-foreground">
            No system prompt defined yet.
          </p>
        ),
      },
    ];

    if (cddiInstructions) {
      summarySections.push({
        title: "CDDI Instructions",
        content: (
          <p className="line-clamp-3 text-sm text-muted-foreground">
            {truncateText(cddiInstructions, 220)}
          </p>
        ),
      });
    }

    const summaryTags: DetailSummaryTag[] = [];
    if (cddiInstructions) {
      summaryTags.push({ label: "CDDI", tone: "info" });
    }
    if (ownerType) {
      summaryTags.push({
        label: ownerType,
        tone: "default",
      });
    }

    const status: DetailSummaryStatus = {
      label: formatIntegrationStatusLabel(statusValue, enabled),
      tone: resolveIntegrationStatusTone(statusValue, enabled),
      icon: resolveIntegrationStatusIcon(statusValue),
      meta: `Updated ${formatIntegrationUpdatedAt(updatedAt)}`,
    };

    return (
      <div
        className="flex h-full flex-col"
        style={{ backgroundColor: "#e8effa" }}
      >
        <div className="flex-1 overflow-y-auto">
          <div className="mx-auto w-full max-w-5xl px-6 py-8 space-y-6">
            {feedbackContent}
            <DetailSummaryCard
              data-testid={`admin-integration-summary-${integration.id}`}
              icon={
                <span className="text-2xl leading-none">
                  {getIntegrationGlyph(integration)}
                </span>
              }
              title={formatIntegrationName(integration)}
              subtitle={`ID: ${integration.id}`}
              description={integration.description}
              tags={summaryTags.length ? summaryTags : undefined}
              status={status}
              sections={summarySections}
              footerStart={
                <>
                  <Badge variant={enabled ? "secondary" : "outline"}>
                    {enabled
                      ? "Enabled for this workgroup"
                      : "Disabled for this workgroup"}
                  </Badge>
                  {visibility && (
                    <Badge variant="outline" className="bg-muted/30">
                      {visibility}
                    </Badge>
                  )}
                </>
              }
              footerEnd={
                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-2"
                  onClick={() => setActiveItemKey("section:workgroupType")}
                >
                  <Layers className="h-4 w-4" />
                  Manage access
                </Button>
              }
            />
            <DetailSection
              icon={<SettingsIcon className="h-5 w-5 text-primary" />}
              title="Configuration"
              subtitle="Manage authentication and connection settings."
              toolbar={
                !enabled ? (
                  <Badge variant="outline" className="bg-amber-50 text-amber-600">
                    Disabled
                  </Badge>
                ) : undefined
              }
            >
              {enabled ? (
                <SettingsPage
                  pageType={integration.id}
                  loading={false}
                  selectedWorkgroupId={currentWorkgroupId || undefined}
                />
              ) : (
                <>
                  <p>
                    Enable this integration for the workgroup to unlock configuration controls.
                  </p>
                  <Button
                    variant="default"
                    onClick={() => setActiveItemKey("section:workgroupType")}
                    className="gap-2 self-start"
                  >
                    <Layers className="h-4 w-4" />
                    Open Integration Access
                  </Button>
                </>
              )}
            </DetailSection>
          </div>
        </div>
      </div>
    );
  };

  let detailContent: React.ReactNode;

  if (workgroupsLoading && !currentWorkgroupId) {
    detailContent = (
      <div className="flex h-full items-center justify-center">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading workgroups…
        </div>
      </div>
    );
  } else if (!currentWorkgroupId) {
    detailContent = (
      <EmptyState
        title="No workgroup selected"
        description="Create a workgroup to get started."
        action={
          isAdmin ? (
            <Button onClick={() => setActiveItemKey("section:createWorkgroup")}>
              Create workgroup
            </Button>
          ) : undefined
        }
      />
    );
  } else if (activeIntegrationId) {
    detailContent = renderIntegrationDetail(activeIntegrationId);
  } else {
    switch (activeSectionId) {
      case "members":
        detailContent = renderMembers();
        break;
      case "workgroupType":
        detailContent = renderIntegrationAccess();
        break;
      case "apiKeys":
        detailContent = renderApiKeys();
        break;
      case "userKeys":
        detailContent = renderUserKeys();
        break;
      case "license":
        detailContent = renderLicense();
        break;
      case "createWorkgroup":
        detailContent = renderCreateWorkgroup();
        break;
      case "deleteWorkgroup":
        detailContent = renderDeleteWorkgroup();
        break;
      default:
        detailContent = (
          <EmptyState
            title="Select an item"
            description="Choose an item from the sidebar to view its details."
          />
        );
        break;
    }
  }

  const masterItemsContent = (
    <div className="pb-6">
      {generalItems.length > 0 && (
        <>
          <SidebarSectionLabel label="Workgroup" />
          {generalItems.map((item) => (
            <MasterListItem
              key={item.key}
              title={item.title}
              subtitle={item.subtitle}
              icon={item.icon}
              badge={item.badge}
              isSelected={activeItemKey === item.key}
              onClick={() => setActiveItemKey(item.key)}
            />
          ))}
        </>
      )}

      {securityItems.length > 0 && (
        <>
          <SidebarSectionLabel label="Security" />
          {securityItems.map((item) => (
            <MasterListItem
              key={item.key}
              title={item.title}
              subtitle={item.subtitle}
              icon={item.icon}
              badge={item.badge}
              isSelected={activeItemKey === item.key}
              onClick={() => setActiveItemKey(item.key)}
            />
          ))}
        </>
      )}

      {integrationItems.length > 0 && (
        <>
          <SidebarSectionLabel label="Integrations" />
          {integrationItems.map((item) => (
            <MasterListItem
              key={item.key}
              title={item.title}
              subtitle={item.subtitle}
              icon={item.icon}
              badge={item.badge}
              isSelected={activeItemKey === item.key}
              onClick={() => setActiveItemKey(item.key)}
            />
          ))}
        </>
      )}

      {integrationItems.length === 0 && isAdmin && (
        <div className="px-4 py-3 text-xs text-muted-foreground">
          {integrationsLoading
            ? "Loading integrations…"
            : "No integrations available for this account."}
        </div>
      )}

      {actionItems.length > 0 && (
        <>
          <SidebarSectionLabel label="Actions" />
          {actionItems.map((item) => (
            <MasterListItem
              key={item.key}
              title={item.title}
              subtitle={item.subtitle}
              icon={item.icon}
              badge={item.badge}
              isSelected={activeItemKey === item.key}
              onClick={() => setActiveItemKey(item.key)}
            />
          ))}
        </>
      )}
    </div>
  );

  const headerControls = (
    <div className="space-y-3">
      <WorkgroupSelect
        workgroups={availableWorkgroups}
        selectedWorkgroup={currentWorkgroupId ?? ""}
        onSelect={handleWorkgroupChange}
        disabled={workgroupsLoading || availableWorkgroups.length <= 1}
        label="Active workgroup"
        emptyMessage="No workgroups available"
        dataTestId="workgroup-select-admin"
      />
      <Button
        variant="ghost"
        size="sm"
        className="w-full justify-start"
        onClick={() => navigate("/")}
      >
        <Home className="h-4 w-4" />
        Back to main
      </Button>
    </div>
  );

  return (
    <div className="h-full bg-background">
      <MasterDetailLayout
        masterTitle="Admin"
        masterItems={masterItemsContent}
        detailContent={detailContent}
        headerControls={headerControls}
        showSearch={false}
        showNewButton={isAdmin}
        newItemLabel="New workgroup"
        onNewItem={() => setActiveItemKey("section:createWorkgroup")}
        masterWidth="w-80"
        className="min-h-[calc(100vh-4rem)]"
        selectedBackground="#e8effa"
      />
    </div>
  );
};

export default AdminPage;
